import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { MedicationPicker, resolveLibrary } from "../../components/MedicationPicker";
import { MED_FORMS as FORMS, MED_ROUTES as ROUTES } from "../../lib/medVocab";
import { ProviderSelect } from "../../components/ProviderSelect";
import { RX_FREQS } from "../../components/rx/rxShared";
import { NewPill } from "../../components/NewPill";
import { FavoritesPicker, SaveFavoriteButton } from "../../components/FavoritesMenu";
import { useRxPanels } from "../../components/rx/useRxPanels";
import { MedSourceTabs, NoCurrentMeds } from "./medlist/MedListHeader";
import { AllergyOverrideModal, PharmacyPromptDialog } from "./medlist/MedListDialogs";
import { MedExtraFields } from "./medlist/MedExtraFields";
import { RefillRequestDialog } from "../../components/rx/RefillRequestDialog";
import { SessionMedsSnapshot } from "../../components/SessionMedsSnapshot";
import { Plus, Pencil, Trash2, ShieldAlert, X, RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
const chip = (color) => `rounded-full px-2 py-0.5 text-[10px] font-bold ${color}`;

const FREQS = RX_FREQS;
const STATUSES = ["Active", "On Hold", "Discontinued", "Completed"];
const STATUS_TONE = { Active: "bg-[#E8F5E9] text-[#2E7D32]", "On Hold": "bg-[#FFF3E0] text-[#8D5A00]",
                      Discontinued: "bg-[#EFEBE0] text-[#6D4C41]", Completed: "bg-[#EFEBE0] text-[#6D4C41]" };

const EMPTY = { medication: "", strength: "", form: "Tablet", route: "Oral (PO)", frequency: "Once daily (QD)",
                catalog_id: "", presentation_id: "",
                daw: "No", refills: "0", days_supply: "", quantity: "",
                prescriber: "", pharmacy: "", ndc: "", dea_schedule: "",
                action_date: "", start_date: "", end_date: "",
                renewal_date: "", directions: "", status: "Active", purpose: "",
                indication: "", prn: false, max_daily_dose: "", premedication: "",
                taper_schedule: "", hold_parameters: "", discontinuation_reason: "",
                adherence_notes: "", notes: "", source: "monitored", outside_prescriber: "",
                samples_given: false, samples_amount: "", risks_benefits_discussed: false, risks_benefits_reason: "" };

const fromFavorite = (f) => ({ medication: f.medication, catalog_id: f.catalog_id, presentation_id: f.presentation_id, strength: f.strength,
  form: f.form, route: f.route, frequency: f.frequency, directions: f.directions, quantity: f.quantity, refills: f.refills || "0",
  days_supply: f.days_supply, max_daily_dose: f.max_daily_dose, daw: f.daw ? "Yes" : "No", prn: !!f.prn });
const yn = (v) => (v ? "Yes" : "No");
const COLS = ["Status", "Action Date", "Medication", "Start", "Form", "Strength", "Route", "Frequency", "DAW", "Refills", "Quantity", "Samples", "Samples Amount", "Risks/Benefits", "End", "Renewal", "Directions"];

export default function MedicationList({ client, editable }) {
  const [items, setItems] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [showOverrides, setShowOverrides] = useState(false);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [ddi, setDdi] = useState(null); // pending {hits, form}
  const [overrideReason, setOverrideReason] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const [refillFor, setRefillFor] = useState(null);
  const [pending, setPending] = useState({});
  const [snap, setSnap] = useState(null);
  const [tab, setTab] = useState("monitored");
  const [allergy, setAllergy] = useState(null); // pending {hits, form}
  const [prompt, setPrompt] = useState(null);
  const [favKey, setFavKey] = useState(0);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const load = () => {
    api.get(`/clients/${client.id}/medications`).then((r) => setItems(r.data)).catch(() => {});
    api.get("/refill-requests", { params: { client_id: client.id, status: "pending" } }).then((r) => setPending(Object.fromEntries(r.data.map((x) => [x.medication_id, x])))).catch(() => {});
  };
  const toggleSnap = () => (snap ? setSnap(null) : api.get(`/clients/${client.id}/session-meds`).then((r) => setSnap(r.data)).catch((e) => toast.error(apiError(e))));
  const { openRx, panels } = useRxPanels(load);
  const loadOverrides = () => api.get(`/clients/${client.id}/medications/ddi-overrides`)
    .then((r) => setOverrides(r.data)).catch(() => {});
  useEffect(() => { load(); loadOverrides(); }, [client.id]); // eslint-disable-line
  const isOutside = (m) => m.source === "outside";
  const counts = { monitored: items.filter((m) => !isOutside(m)).length, outside: items.filter(isOutside).length };
  const currentCount = items.filter((m) => ["Active", "On Hold"].includes(m.status || "Active")).length;
  const orig = items.find((m) => m.id === editId);
  const linkedLocked = !!(orig?.prescription_id && orig.status === "Discontinued");
  const filtered = useMemo(() => items.filter((m) => {
    if ((tab === "outside") !== isOutside(m)) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [m.medication, m.strength, m.route, m.frequency, m.status, m.prescriber, m.outside_prescriber].some((x) => (x || "").toLowerCase().includes(s));
  }), [items, q, tab]);

  const finalizeSave = async (raw) => {
    try {
      const payload = await resolveLibrary(raw);
      const r = editId ? await api.put(`/medications/${editId}`, payload) : await api.post(`/clients/${client.id}/medications`, payload);
      toast.success(editId ? "Medication updated" : "Medication added");
      setPanel(false); setDdi(null); setOverrideReason(""); load();
      if (r.data?.pharmacy_prompt) setPrompt(r.data.pharmacy_prompt);
    } catch (err) { toast.error(apiError(err)); }
  };

  const proceedWithOverride = async () => {
    if (!ddi) return;
    const reason = overrideReason.trim();
    if (!reason || reason.length < 8) {
      return toast.error("A clinical rationale of at least 8 characters is required to log the override");
    }
    setSavingOverride(true);
    try {
      await api.post(`/clients/${client.id}/medications/ddi-override`, {
        medication: ddi.form.medication,
        interactions: ddi.hits,
        reason,
      });
      toast.success("Override logged to audit trail");
      await finalizeSave(ddi.form);
      loadOverrides();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSavingOverride(false); }
  };

  const runDdi = async (fm) => {
    // Only run DDI check on new / active medications to keep the flow fast
    if (!editId && (fm.status || "Active") === "Active") {
      try {
        const r = await api.post(`/clients/${client.id}/medications/check-interactions`,
          { medication: fm.medication, exclude_id: editId || "" });
        const hits = r.data?.interactions || [];
        const severe = hits.filter((h) => h.severity === "contraindicated" || h.severity === "major");
        if (hits.length > 0) {
          setDdi({ hits, severe, form: { ...fm } });
          return;
        }
      } catch { /* fall through, network / rate errors should not block save */ }
    }
    await finalizeSave(fm);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.medication.trim()) return toast.error("Medication name is required");
    const changed = !orig || orig.medication !== form.medication || (orig.catalog_id || "") !== (form.catalog_id || "");
    if (changed && ["Active", "On Hold"].includes(form.status || "Active")) {
      try {
        const r = await api.post(`/clients/${client.id}/medications/check-allergies`, { medication: form.medication, catalog_id: form.catalog_id || "" });
        if (r.data.hits.length) { setAllergy({ hits: r.data.hits, form: { ...form } }); return; }
      } catch (err) { return toast.error(apiError(err)); }
    }
    await runDdi(form);
  };
  const remove = async () => {
    try { await api.delete(`/medications/${del.id}`); toast.success("Medication removed"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="medication-list-panel">
      {overrides.length > 0 && (
        <div className="mb-3 rounded-2xl border border-[#E5D3D0] bg-[#FFF8F5] p-3" data-testid="ddi-override-strip">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-start gap-2">
              <ShieldAlert size={16} className="text-[#8D5A00] mt-0.5" />
              <div>
                <div className="text-xs font-bold text-[#3E2723]">
                  {overrides.length} interaction override{overrides.length === 1 ? "" : "s"} logged for this client
                </div>
                <div className="text-[11px] text-[#7B6157]">Supervisors can review overrides for clinical rationale and severity.</div>
              </div>
            </div>
            <button data-testid="ddi-overrides-toggle"
              onClick={() => setShowOverrides((s) => !s)}
              className="rounded-full border border-[#E5DFD3] bg-white px-3 py-1 text-[11px] font-bold text-[#4E342E] hover:bg-[#F5F2EB]">
              {showOverrides ? "Hide review pane" : "Show review pane"}
            </button>
          </div>
          {showOverrides && (
            <div className="mt-3 space-y-2" data-testid="ddi-overrides-panel">
              {overrides.map((o) => (
                <div key={o.id} data-testid={`ddi-override-row-${o.id}`}
                  className="rounded-xl border border-[#EFEBE0] bg-white p-3">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={chip(
                      o.top_severity === "contraindicated" ? "bg-[#C62828] text-white" :
                      o.top_severity === "major" ? "bg-[#B26A00] text-white" :
                      o.top_severity === "moderate" ? "bg-[#FFB300] text-[#3E2723]" :
                      "bg-[#EFEBE0] text-[#6D4C41]"
                    )}>{o.top_severity || "unknown"}</span>
                    <span className="text-sm font-bold text-[#3E2723]">{o.medication}</span>
                    <span className="text-[11px] text-[#7B6157]">{o.interaction_count} interaction{o.interaction_count === 1 ? "" : "s"}</span>
                    <span className="ml-auto text-[11px] text-[#7B6157]">{o.clinician_name} · {fmt(o.created_at)}</span>
                  </div>
                  <div className="text-[12px] text-[#5D4037] mb-1">
                    <span className="font-semibold">Effect:</span>{" "}
                    {o.interactions?.map((h) => `${h.other} - ${h.effect}`).join("; ")}
                  </div>
                  <div className="text-[12px] text-[#5D4037] mb-2"><span className="font-semibold">Rationale:</span> {o.reason || "-"}</div>
                  {o.reviewed_at ? (
                    <div className="text-[11px] text-[#2E7D32]">Reviewed by {o.reviewed_by_name} on {fmt(o.reviewed_at)}</div>
                  ) : (
                    <div className="flex justify-end">
                      <button data-testid={`ddi-override-review-${o.id}`}
                        onClick={async () => {
                          try { await api.post(`/medical/ddi-overrides/${o.id}/review`); toast.success("Marked reviewed"); loadOverrides(); }
                          catch (err) { toast.error(apiError(err)); }
                        }}
                        className="rounded-full bg-[#4E342E] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#3E2723]">
                        Mark reviewed
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#3E2723]">Master Medication List</h3>
          <p className="text-xs text-[#7B6157]">{filtered.length} entr{filtered.length === 1 ? "y" : "ies"} on this tab</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <NoCurrentMeds client={client} currentCount={currentCount} editable={editable} reloadKey={items.length} />
          <Btn variant="outline" data-testid="session-meds-toggle" aria-expanded={!!snap} onClick={toggleSnap}>{snap ? "Hide latest session meds" : "Latest session meds"}</Btn>
          <input data-testid="medication-filter" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="rounded-full border border-[#E5DFD3] bg-white px-3.5 py-1.5 text-xs outline-none w-44 focus:border-[#8D6E63]" />
          {editable && <Btn data-testid="add-medication-btn" variant="outline"
            onClick={() => { setForm({ ...EMPTY, source: tab }); setEditId(null); setFavKey((k) => k + 1); setPanel(true); }}><Plus size={14} /> {tab === "outside" ? "Add Outside Medication" : "Add New Master Medication"}</Btn>}
        </div>
      </div>
      {snap && <div className="mb-3"><SessionMedsSnapshot data={snap} /></div>}
      <div className="mb-3"><MedSourceTabs tab={tab} setTab={setTab} counts={counts} /></div>

      {filtered.length === 0 ? <Empty text={tab === "outside" ? "No outside medications on file" : "No medications on file"} /> : (
        <div className="max-w-full overflow-x-auto rounded-2xl border border-[#EFEBE0]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FDFCF8]">
              <tr className="text-left text-[10px] font-bold uppercase text-[#7B6157]">
                {[...COLS, ...(tab === "outside" ? ["Outside Prescriber"] : []), "Actions"].map((h) => (
                  <th key={h} className={`px-3 py-3 whitespace-nowrap ${h === "Actions" ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} data-testid={`medication-row-${m.id}`} className="border-t border-[#EFEBE0]">
                  <td className="px-3 py-2"><span className={chip(STATUS_TONE[m.status] || STATUS_TONE.Active)}>{m.status || "Active"}</span></td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(m.action_date || m.updated_at)}</td>
                  <td className="px-3 py-2 font-semibold text-[#3E2723] whitespace-nowrap">{m.medication}
                    {m.rx_number && <div className="font-mono text-[9px] font-normal text-[#84675B]">From {m.rx_number}</div>}
                    {m.allergy_override && <div className="flex items-center gap-1 text-[10px] font-semibold text-[#8D5A00]" title={`${m.allergy_override.note} (${m.allergy_override.by})`} data-testid={`medication-allergy-override-${m.id}`}><ShieldAlert size={10} /> Allergy override</div>}</td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(m.start_date)}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{m.form || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{m.strength || m.dosage || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{m.route || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{m.frequency || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{m.daw || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157] text-center">
                    <span data-testid={`medication-refills-cell-${m.id}`}>{m.refills || "-"}</span>
                    {m.refills_approved_total > 0 && <div className="mt-0.5 text-[10px] font-semibold text-[#2E7D32]" data-testid={`medication-refills-approved-${m.id}`}>+{m.refills_approved_total} approved</div>}
                    {m.last_refill_at && <div className="text-[10px] text-[#7B6157]" title={`Last refill approved by ${m.last_refill_by || ""}`}>Last: {dayjs(m.last_refill_at).format("MM/DD")}</div>}
                    {pending[m.id] && <div className="text-[10px] font-bold text-[#8D5A00]" data-testid={`medication-refill-pending-${m.id}`}>Request pending</div>}
                  </td>
                  <td className="px-3 py-2 text-[#7B6157]">{m.quantity || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{yn(m.samples_given)}</td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{m.samples_amount || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap" title={m.risks_benefits_reason || undefined}>{m.risks_benefits_discussed === true ? "Yes" : m.risks_benefits_reason ? "No, see reason" : m.risks_benefits_discussed === false ? "No" : "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(m.end_date)}</td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(m.renewal_date)}</td>
                  <td className="px-3 py-2 text-[#7B6157] max-w-[220px] truncate" title={m.directions}>{m.directions || m.notes || "-"}</td>
                  {tab === "outside" && <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{m.outside_prescriber || "-"}</td>}
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {editable && (
                        <>
                          {["Active", "On Hold"].includes(m.status || "Active") && !pending[m.id] && <button data-testid={`refill-medication-${m.id}`}
                            title="Log refill request" aria-label="Log refill request"
                            onClick={() => setRefillFor(m)}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#4E342E] hover:bg-[#EDE7DC]"><RefreshCw size={13} /></button>}
                          <button data-testid={`edit-medication-${m.id}`} onClick={() => { setForm({ ...EMPTY, ...m }); setEditId(m.id); setFavKey((k) => k + 1); setPanel(true); }}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                          {!m.prescription_id && <button data-testid={`delete-medication-${m.id}`} onClick={() => setDel(m)}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Master Medication" : "Add New Master Medication"} testId="medication-slide-panel">
        <form onSubmit={submit} className="space-y-4 p-1">
          <div><div className="mb-1 flex flex-wrap items-center justify-between gap-2"><FLabel><span className="inline-flex items-center gap-1.5">Medication and strength * <NewPill feature="medication-library" /></span></FLabel>
            <div className="flex items-center gap-3"><span className="inline-flex items-center gap-1.5"><FavoritesPicker testId="medication-favorites" onPick={(fv) => { setForm((s) => ({ ...s, ...fromFavorite(fv) })); setFavKey((k) => k + 1); }} /><NewPill feature="med-favorites" /></span>
              <SaveFavoriteButton testId="medication-save-favorite" value={form} /></div></div>
            <MedicationPicker key={favKey} testId="medication-name" value={form}
              onChange={(patch) => setForm((s) => ({ ...s, ...patch, indication: s.indication }))} />
          </div>
          <MedExtraFields form={form} setForm={setForm} linked={!!orig?.prescription_id} />
          {linkedLocked && <p className="flex items-center gap-1.5 rounded-xl bg-[#EFEBE0] px-3 py-2 text-xs font-semibold text-[#6D4C41]" data-testid="medication-locked-note"><Lock size={12} /> Discontinued from {orig.rx_number}. The prescription line is locked. Write a new prescription to restart this medication.</p>}
          {orig?.prescription_id && !linkedLocked && form.status === "Discontinued" && <p className="rounded-xl bg-[#FFF3E0] px-3 py-2 text-xs text-[#8D5A00]" data-testid="medication-discontinue-hint">Saving locks this medication on {orig.rx_number} so it cannot be printed, emailed, or duplicated. A discontinuation reason is required, and you will be asked to call the pharmacy to cancel remaining refills.</p>}
          <div className="grid grid-cols-2 gap-3">
            {form.medication && !form.catalog_id && !form._custom && (
              <>
                <div><FLabel>Strength</FLabel><FInput data-testid="medication-strength" value={form.strength} onChange={f("strength")} placeholder="50 mg" /></div>
                <div><FLabel>Form</FLabel><FSelect data-testid="medication-form" value={form.form} onChange={f("form")}>{FORMS.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
                <div><FLabel>Route</FLabel><FSelect data-testid="medication-route" value={form.route} onChange={f("route")}>{ROUTES.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
              </>
            )}
            <div><FLabel>Frequency</FLabel><FSelect data-testid="medication-frequency" value={form.frequency} onChange={f("frequency")}>{FREQS.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>DAW (Dispense as Written)</FLabel><FSelect data-testid="medication-daw" value={form.daw} onChange={f("daw")}><option>No</option><option>Yes</option></FSelect></div>
            <div><FLabel>Refills</FLabel><FInput data-testid="medication-refills" type="number" min="0" value={form.refills} onChange={f("refills")} /></div>
            <div><FLabel>Start Date</FLabel><DateField testId="medication-start" value={form.start_date} onChange={(v) => setForm((s) => ({ ...s, start_date: v }))} /></div>
            <div><FLabel>End Date</FLabel><DateField testId="medication-end" value={form.end_date} onChange={(v) => setForm((s) => ({ ...s, end_date: v }))} /></div>
            <div><FLabel>Renewal Date</FLabel><DateField testId="medication-renewal" value={form.renewal_date} onChange={(v) => setForm((s) => ({ ...s, renewal_date: v }))} /></div>
            <div><FLabel>Prescriber</FLabel>
              <ProviderSelect testId="medication-prescriber" value={form.prescriber}
                onChange={(v) => setForm((s) => ({ ...s, prescriber: v }))}
                placeholder="Search prescribers by name, org, or NPI" />
            </div>
            <div><FLabel>Status</FLabel><FSelect data-testid="medication-status" value={form.status} disabled={linkedLocked} onChange={f("status")}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</FSelect></div>
            <div><FLabel>Action Date</FLabel><DateField testId="medication-action-date" value={form.action_date} onChange={(v) => setForm((s) => ({ ...s, action_date: v }))} /></div>
            <div><FLabel>Days Supply</FLabel><FInput data-testid="medication-days-supply" type="number" min="0" value={form.days_supply} onChange={f("days_supply")} placeholder="e.g., 30" /></div>
            <div><FLabel>Quantity</FLabel><FInput data-testid="medication-quantity" type="number" min="0" value={form.quantity} onChange={f("quantity")} placeholder="e.g., 60" /></div>
            <div><FLabel>DEA Schedule</FLabel><FSelect data-testid="medication-dea" value={form.dea_schedule} placeholder="None" onChange={f("dea_schedule")}>
              {["II", "III", "IV", "V", "N/A"].map((s) => <option key={s} value={s}>{s}</option>)}
            </FSelect></div>
            <div><FLabel>Pharmacy</FLabel><FInput data-testid="medication-pharmacy" value={form.pharmacy} onChange={f("pharmacy")} placeholder="e.g., CVS Charlotte" /></div>
            <div><FLabel>NDC Code</FLabel><FInput data-testid="medication-ndc" value={form.ndc} onChange={f("ndc")} placeholder="00093-5165-01" /></div>
            <div><FLabel>Indication</FLabel><FInput data-testid="medication-indication" value={form.indication} onChange={f("indication")} placeholder="Linked problem or clinical rationale" /></div>
            <div><FLabel>Max Daily Dose</FLabel><FInput data-testid="medication-max-daily-dose" value={form.max_daily_dose} onChange={f("max_daily_dose")} placeholder="e.g., 200 mg / 24h" /></div>
            <div className="col-span-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" data-testid="medication-prn" checked={!!form.prn} onChange={(e) => setForm((s) => ({ ...s, prn: e.target.checked }))}
                  className="h-4 w-4 rounded border-[#E5DFD3] text-[#4E342E]" />
                <span className="text-sm text-[#5D4037]">PRN (as needed) medication</span>
              </label>
            </div>
          </div>
          <div><FLabel>Directions</FLabel><FTextarea data-testid="medication-directions" rows={2} value={form.directions} onChange={f("directions")} placeholder="Take one tablet by mouth every morning with food" /></div>
          <div><FLabel>Pre-medication (if required)</FLabel><FTextarea data-testid="medication-premedication" rows={2} value={form.premedication} onChange={f("premedication")} placeholder="e.g., 25 mg diphenhydramine PO 30 minutes before infusion" /></div>
          <div><FLabel>Taper or Titration Schedule</FLabel><FTextarea data-testid="medication-taper" rows={2} value={form.taper_schedule} onChange={f("taper_schedule")} placeholder="e.g., 50 mg for 3 days, then 100 mg thereafter" /></div>
          <div><FLabel>Hold Parameters</FLabel><FTextarea data-testid="medication-hold" rows={2} value={form.hold_parameters} onChange={f("hold_parameters")} placeholder="e.g., Hold if SBP less than 100 or HR less than 55" /></div>
          <div><FLabel>Discontinuation Reason</FLabel><FInput data-testid="medication-disc-reason" value={form.discontinuation_reason} onChange={f("discontinuation_reason")} placeholder="Required when status is Discontinued" /></div>
          <div><FLabel>Adherence Notes</FLabel><FTextarea data-testid="medication-adherence" rows={2} value={form.adherence_notes} onChange={f("adherence_notes")} placeholder="Reported adherence, missed doses, side effects" /></div>
          <div><FLabel>Notes</FLabel><FTextarea data-testid="medication-notes" rows={2} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="medication-save" type="submit">{editId ? "Save Changes" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={remove}
        title="Remove medication" description={`Delete ${del?.medication}${del?.strength ? ` ${del.strength}` : ""} from this record?`} />

      {ddi && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4" data-testid="ddi-warning-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className={`rounded-t-2xl p-4 ${ddi.severe.length > 0 ? "bg-[#C62828]" : "bg-[#B26A00]"}`}>
              <div className="flex items-start gap-3 text-white">
                <ShieldAlert size={22} className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase opacity-80">Drug-Drug Interaction Alert</div>
                  <div className="font-heading text-lg font-bold">
                    {ddi.hits.length} interaction{ddi.hits.length === 1 ? "" : "s"} detected for {ddi.form.medication}
                  </div>
                </div>
                <button data-testid="ddi-close" onClick={() => { setDdi(null); setOverrideReason(""); }} className="rounded-full p-1 hover:bg-white/10"><X size={18} /></button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto p-4 space-y-3">
              {ddi.hits.map((h, i) => (
                <div key={i} data-testid={`ddi-hit-${i}`}
                  className={`rounded-xl border p-3 ${
                    h.severity === "contraindicated" ? "border-[#C62828] bg-[#FFEBEE]" :
                    h.severity === "major" ? "border-[#FFB300] bg-[#FFF3E0]" :
                    h.severity === "moderate" ? "border-[#FFECB3] bg-[#FFF8E1]" :
                    "border-[#EFEBE0] bg-[#FDFCF8]"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      h.severity === "contraindicated" ? "bg-[#C62828] text-white" :
                      h.severity === "major" ? "bg-[#B26A00] text-white" :
                      h.severity === "moderate" ? "bg-[#FFB300] text-[#3E2723]" :
                      "bg-[#EFEBE0] text-[#6D4C41]"}`}>{h.severity}</span>
                    <span className="text-sm font-bold text-[#3E2723]">with {h.other}</span>
                  </div>
                  <div className="text-xs text-[#5D4037] mb-1"><strong>Effect:</strong> {h.effect}</div>
                  <div className="text-xs text-[#5D4037]"><strong>Management:</strong> {h.management}</div>
                </div>
              ))}
              <div className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3">
                <label className="mb-1 block text-[10px] font-bold uppercase text-[#7B6157]" htmlFor="ddi-override-reason">
                  Clinical rationale (required to override, min 8 characters)
                </label>
                <textarea id="ddi-override-reason" data-testid="ddi-override-reason" rows={2}
                  value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Why is the benefit greater than the interaction risk here?"
                  className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
                <div className="mt-1 text-[10px] text-[#7B6157]">
                  This override will be logged to the audit trail with your name, the severity, and each interaction effect.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#F1EEE7] p-4">
              <Btn variant="ghost" data-testid="ddi-cancel" onClick={() => { setDdi(null); setOverrideReason(""); }}>Review Again</Btn>
              <Btn data-testid="ddi-proceed" onClick={proceedWithOverride} disabled={savingOverride}>
                {savingOverride ? "Logging override…" : "Add anyway with override"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {allergy && <AllergyOverrideModal pending={allergy} onCancel={() => setAllergy(null)}
        onProceed={(note) => { const fm = { ...allergy.form, allergy_override_note: note }; setAllergy(null); runDdi(fm); }} />}
      {prompt && <PharmacyPromptDialog prompt={prompt} onClose={() => setPrompt(null)} onOpenRx={(id) => { setPrompt(null); openRx(id); }} />}
      {panels}

      {refillFor && <RefillRequestDialog client={client} med={refillFor} onClose={() => setRefillFor(null)} onDone={() => { setRefillFor(null); load(); }} />}
    </div>
  );
}
